import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Layers } from "lucide-react";

import { MarketingLayout, Section, Eyebrow, TrialButton, useSeo } from "@/components/marketing/MarketingLayout";
import { publicService } from "@/services/publicService";
import { PILLARS, FRAGMENTS, HOW_IT_WORKS, RESA_CAPABILITIES } from "@/config/marketing";
import { Button } from "@/components/ui/button";

export function FoundingBadge({ compact = false }) {
  const [f, setF] = useState(null);
  useEffect(() => { publicService.foundingStatus().then(setF).catch(() => setF(null)); }, []);
  if (!f || !f.enrollment_open || f.remaining <= 0) return null;
  return (
    <span data-testid="founding-badge"
      className={`inline-flex items-center gap-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold text-brand-navy ${compact ? "" : "mt-4"}`}>
      <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
      {f.remaining} of {f.cap} Founding Agent spots remaining · $19/mo locked
    </span>
  );
}

export default function Home() {
  useSeo({ title: "Built for the Business of Real Estate", path: "/",
    description: "AgentHQ by RESIDENNCE — the business operating system for real estate professionals. Bring relationships, follow-ups, transactions, marketing, compliance and goals into one connected workspace." });
  return (
    <MarketingLayout>
      {/* Hero */}
      <div className="relative overflow-hidden bg-brand-navy text-white">
        <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-brand-deep-blue/50 blur-3xl" />
        <Section className="relative py-20 sm:py-28" data-testid="home-hero">
          <div className="max-w-3xl">
            <Eyebrow>The business operating system for real estate</Eyebrow>
            <h1 className="mt-4 font-brand text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
              Built for the Business of Real Estate.
            </h1>
            <p className="mt-5 max-w-xl text-lg text-white/70">
              Everything behind your real estate business, working together. Residennce brings the operational side of your business into one connected system — so you can spend less time switching tools and more time on what needs attention.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <TrialButton />
              <Link to="/demo"><Button variant="outline" className="border-white/25 bg-white/5 text-white hover:bg-white/10" data-testid="cta-see-action">See AgentHQ in Action <ArrowRight className="h-4 w-4" /></Button></Link>
            </div>
            <FoundingBadge />
          </div>
        </Section>
      </div>

      {/* Problem */}
      <Section className="py-20" data-testid="home-problem">
        <div className="max-w-2xl">
          <Eyebrow>The problem</Eyebrow>
          <h2 className="mt-3 font-brand text-3xl font-bold text-brand-navy sm:text-4xl">Your real estate business shouldn't live in six different places.</h2>
          <p className="mt-4 text-base text-charcoal/70">Leads here, follow-ups there, transactions in a spreadsheet, deadlines in your head. AgentHQ is the operating layer that connects the moving pieces.</p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {FRAGMENTS.map((f) => (
            <span key={f} className="rounded-full border border-black/10 bg-brand-light-gray px-4 py-2 text-sm font-medium text-charcoal/70">{f}</span>
          ))}
        </div>
      </Section>

      {/* Category positioning */}
      <div className="bg-brand-light-gray">
        <Section className="py-20" data-testid="home-category">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Eyebrow>Category</Eyebrow>
              <h2 className="mt-3 font-brand text-3xl font-bold text-brand-navy sm:text-4xl">The Business Operating System for Real Estate Professionals.</h2>
              <p className="mt-4 text-base text-charcoal/70">Residennce doesn't just manage your contacts. It helps manage your business.</p>
              <p className="mt-2 text-base font-medium text-brand-navy">Your CRM knows your leads. Residennce knows your business.</p>
            </div>
            <div className="rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
              <Layers className="h-8 w-8 text-brand-gold" />
              <p className="mt-3 font-semibold text-brand-navy">One connected workspace</p>
              <p className="mt-1 text-sm text-charcoal/60">RUN · SELL · MARKET · PROTECT · GROW — plus RESA, the intelligence built in.</p>
            </div>
          </div>
        </Section>
      </div>

      {/* Five pillars */}
      <Section className="py-20" data-testid="home-pillars">
        <Eyebrow>The platform</Eyebrow>
        <h2 className="mt-3 font-brand text-3xl font-bold text-brand-navy sm:text-4xl">Five areas, one system.</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.key} className="rounded-2xl border border-black/10 bg-white p-6 transition-shadow hover:shadow-md" data-testid={`pillar-${p.key}`}>
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-navy text-brand-gold"><Icon className="h-5 w-5" /></div>
                <p className="mt-4 font-brand text-lg font-bold tracking-wide text-brand-navy">{p.label}</p>
                <p className="text-sm font-medium text-charcoal/60">{p.tagline}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.items.map((i) => <li key={i} className="flex items-center gap-2 text-sm text-charcoal/80"><Check className="h-3.5 w-3.5 text-brand-gold" /> {i}</li>)}
                </ul>
                {p.note && <p className="mt-3 text-xs text-charcoal/50">{p.note}</p>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* RESA teaser */}
      <div className="bg-brand-navy text-white">
        <Section className="py-20" data-testid="home-resa">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Eyebrow>Intelligence, built in</Eyebrow>
              <h2 className="mt-3 font-brand text-3xl font-bold sm:text-4xl">Meet RESA.</h2>
              <p className="mt-2 text-sm font-semibold uppercase tracking-widest text-brand-soft-gold">Real Estate Strategic Assistant</p>
              <p className="mt-4 text-white/70">RESA is intelligence built into AgentHQ — not a detached chatbot. It works from your authorized business context and always prepares actions for your approval.</p>
              <Link to="/resa" className="mt-5 inline-flex items-center gap-1.5 text-brand-soft-gold hover:underline">Explore RESA <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <ul className="space-y-3">
              {RESA_CAPABILITIES.map((c) => (
                <li key={c} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" /> {c}</li>
              ))}
            </ul>
          </div>
        </Section>
      </div>

      {/* How it works */}
      <Section className="py-20" data-testid="home-how">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="mt-3 font-brand text-3xl font-bold text-brand-navy sm:text-4xl">From signup to a business that runs itself into focus.</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.n} className="rounded-2xl border border-black/10 bg-white p-6">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-brand-gold font-brand text-lg font-bold text-brand-navy">{s.n}</div>
              <p className="mt-4 font-semibold text-brand-navy">{s.title}</p>
              <p className="mt-1 text-sm text-charcoal/70">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <div className="bg-brand-light-gray">
        <Section className="py-20 text-center" data-testid="home-cta">
          <h2 className="mx-auto max-w-2xl font-brand text-3xl font-bold text-brand-navy sm:text-4xl">Bring the business of your business together.</h2>
          <p className="mx-auto mt-3 max-w-xl text-charcoal/70">Start your 14-day free trial. A payment method is required; billing begins after your trial unless you cancel.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <TrialButton className="bg-brand-navy text-white hover:bg-brand-deep-blue" />
            <Link to="/pricing"><Button variant="outline">View pricing</Button></Link>
          </div>
          <div className="mt-5 flex justify-center"><FoundingBadge compact /></div>
        </Section>
      </div>
    </MarketingLayout>
  );
}
